import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import type { AdminUnit, PublicResident } from '@residenone/shared';
import { listAdminUnits } from '../../lib/units-api';
import { listResidents } from '../../lib/residents-api';
import {
  BarPlot,
  ChartsAxisHighlight,
  ChartsGrid,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
  PieChart,
  ResponsiveChartContainer,
} from '@mui/x-charts';
import { buildResidentsCsv, signupTrend, summarizePeople } from '../../lib/people-summary';
import { downloadCsv } from '../../lib/annual-export';
import { ChartLegend } from '../../components/ChartLegend';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';
import { chartColors } from '../../theme';

function monthLabel(ym: string, withYear = false): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString('id-ID', { month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
}

function StatCard({ label, value, sub, to, tone }: { label: string; value: ReactNode; sub?: ReactNode; to?: string; tone?: 'warning' }) {
  const body = (
    <CardContent sx={{ height: '100%' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="h5"
        fontWeight={600}
        sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums', color: tone === 'warning' ? 'warning.main' : 'text.primary' }}
      >
        {value}
      </Typography>
      {sub ? <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{sub}</Typography> : null}
    </CardContent>
  );
  return (
    <Card
      variant="outlined"
      sx={{ transition: 'box-shadow 200ms ease, transform 200ms ease', ...(to ? { '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } } : {}) }}
    >
      {to ? <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>{body}</CardActionArea> : body}
    </Card>
  );
}

export function PeopleSummaryPage() {
  const t = useT();
  const theme = useTheme();
  const [units, setUnits] = useState<AdminUnit[] | null>(null);
  const [residents, setResidents] = useState<PublicResident[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listAdminUnits(), listResidents()])
      .then(([u, r]) => {
        setUnits(u);
        setResidents(r);
      })
      .catch((e) => setError(extractApiError(e).message));
  }, []);

  const summary = useMemo(() => (units && residents ? summarizePeople(units, residents) : null), [units, residents]);
  const trend = useMemo(() => signupTrend(residents ?? [], new Date()), [residents]);
  const statusSlices = summary
    ? [
        { key: 'active', label: t('res.chipActive'), value: summary.residentsActive, color: theme.palette.success.main },
        { key: 'pending', label: t('res.chipPending'), value: summary.residentsPending, color: theme.palette.warning.main },
        { key: 'suspended', label: t('res.chipSuspended'), value: summary.residentsSuspended, color: theme.palette.error.main },
      ]
    : [];

  const download = () => {
    if (!units || !residents) return;
    const csv = buildResidentsCsv(units, residents, {
      block: t('ppl.csvBlock'),
      number: t('ppl.csvNumber'),
      unit: t('ppl.csvUnit'),
      type: t('ppl.csvType'),
      occupancy: t('ppl.csvOccupancy'),
      name: t('ppl.csvName'),
      email: t('ppl.csvEmail'),
      status: t('ppl.csvStatus'),
      primary: t('ppl.csvPrimary'),
      since: t('ppl.csvSince'),
      yes: t('ppl.csvYes'),
      no: t('ppl.csvNo'),
      types: { HOUSE: t('units.typeHouse'), APARTMENT: t('units.typeApartment'), RUKO: t('units.typeRuko') },
      occupancies: { OCCUPIED: t('units.occupiedChip'), VACANT: t('units.vacantChip'), OWNER_ABSENT: t('units.occupiedChip') },
      statuses: { ACTIVE: t('res.chipActive'), PENDING: t('res.chipPending'), SUSPENDED: t('res.chipSuspended') },
    });
    downloadCsv(`data-warga-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  // Occupied first (the normal case), then vacant.
  const segments = [
    { key: 'occupied', label: t('units.occupiedChip'), color: chartColors(theme.palette.mode).primary },
    { key: 'vacant', label: t('units.vacantChip'), color: alpha(theme.palette.text.primary, 0.18) },
  ] as const;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
        <div>
          <Typography variant="h4" fontWeight={600}>{t('ppl.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('ppl.subtitle')}</Typography>
        </div>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={download} disabled={!summary}>
          {t('ppl.download')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        {summary ? (
          <>
            <StatCard
              label={t('ppl.kpiUnits')}
              value={summary.units}
              sub={(['HOUSE', 'APARTMENT', 'RUKO'] as const)
                .filter((k) => summary.byType[k] > 0)
                .map((k) => `${summary.byType[k]} ${t(k === 'HOUSE' ? 'units.typeHouse' : k === 'APARTMENT' ? 'units.typeApartment' : 'units.typeRuko').toLowerCase()}`)
                .join(' · ')}
              to="/units"
            />
            <StatCard
              label={t('ppl.kpiOccupied')}
              value={
                <>
                  {summary.occupied}
                  <Typography component="span" variant="body1" color="text.secondary">{' / '}{summary.units}</Typography>
                </>
              }
              sub={t('ppl.kpiOccupiedSub', { vacant: summary.vacant })}
            />
            <StatCard
              label={t('ppl.kpiResidents')}
              value={summary.residentsActive}
              sub={summary.residentsPending > 0 ? t('ppl.kpiPending', { count: summary.residentsPending }) : t('ppl.kpiNoPending')}
              to="/residents"
            />
            <StatCard
              label={t('ppl.kpiNoResident')}
              value={summary.occupiedWithoutResident.length}
              tone={summary.occupiedWithoutResident.length > 0 ? 'warning' : undefined}
              sub={t('ppl.kpiNoResidentSub')}
            />
          </>
        ) : (
          !error && [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={112} sx={{ borderRadius: 4.5 }} />)
        )}
      </Box>

      {summary && residents ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
          {/* Registrations per month as bars, the running total as a line. */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>{t('ppl.signupsTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ppl.signupsSub', { count: trend.reduce((n, m) => n + m.joined, 0) })}
              </Typography>
              <Box sx={{ mx: -1 }}>
                <ResponsiveChartContainer
                  height={260}
                  margin={{ top: 16, right: 44, bottom: 28, left: 40 }}
                  xAxis={[{ id: 'months', scaleType: 'band', data: trend.map((m) => m.month), valueFormatter: (m: string, ctx) => monthLabel(m, ctx.location === 'tooltip') }]}
                  yAxis={[
                    { id: 'joined', max: Math.max(4, ...trend.map((m) => m.joined)) * 1.6, tickMinStep: 1 },
                    { id: 'total', min: 0, tickMinStep: 1 },
                  ]}
                  series={[
                    {
                      type: 'bar',
                      id: 'joined',
                      label: t('ppl.seriesJoined'),
                      yAxisId: 'joined',
                      color: chartColors(theme.palette.mode).primary,
                      data: trend.map((m) => m.joined),
                    },
                    {
                      type: 'line',
                      id: 'total',
                      label: t('ppl.seriesTotal'),
                      yAxisId: 'total',
                      color: theme.palette.text.primary,
                      showMark: true,
                      data: trend.map((m) => m.total),
                    },
                  ]}
                >
                  <ChartsGrid horizontal />
                  <BarPlot borderRadius={4} />
                  <LinePlot />
                  <MarkPlot />
                  <ChartsXAxis axisId="months" />
                  <ChartsYAxis axisId="joined" position="left" />
                  <ChartsYAxis axisId="total" position="right" />
                  <ChartsAxisHighlight x="band" />
                  <ChartsTooltip trigger="axis" />
                </ResponsiveChartContainer>
              </Box>
              <ChartLegend
                items={[
                  { label: t('ppl.seriesJoined'), color: chartColors(theme.palette.mode).primary },
                  { label: t('ppl.seriesTotal'), color: theme.palette.text.primary, kind: 'line' },
                ]}
              />
            </CardContent>
          </Card>

          {/* Residents by status as a donut, the total in the middle. */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>{t('ppl.statusTitle')}</Typography>
              <Box sx={{ position: 'relative', height: 220, mt: 1 }}>
                <PieChart
                  height={220}
                  margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  slotProps={{ legend: { hidden: true } }}
                  series={[
                    {
                      innerRadius: 62,
                      outerRadius: 96,
                      paddingAngle: 2,
                      cornerRadius: 4,
                      highlightScope: { fade: 'global', highlight: 'item' },
                      faded: { additionalRadius: -4, color: 'gray' },
                      data: statusSlices.map((s) => ({ id: s.key, value: s.value, label: s.label, color: s.color })),
                    },
                  ]}
                />
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <Typography variant="h5" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>{residents.length}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('ppl.statusTotal')}</Typography>
                </Box>
              </Box>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {statusSlices.map((s) => (
                  <Stack key={s.key} direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{s.label}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {residents.length > 0 ? `${Math.round((s.value / residents.length) * 100)}%` : '—'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {summary && summary.blocks.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2, alignItems: 'start' }}>
          {/* Each block as one bar split by occupancy, with its registered residents. */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>{t('ppl.blocksTitle')}</Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5, mb: 2 }} flexWrap="wrap" useFlexGap>
                {segments.map((s) => (
                  <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack spacing={1.5}>
                {summary.blocks.map((b) => (
                  // Desktop: name | bar | counts. Phones: name and counts, the bar full width below.
                  <Box
                    key={b.block}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr auto', sm: '64px 1fr 150px' },
                      gridTemplateAreas: { xs: '"name counts" "bar bar"', sm: '"name bar counts"' },
                      alignItems: 'center',
                      columnGap: 1.5,
                      rowGap: 0.75,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ gridArea: 'name' }} noWrap>
                      {b.block}
                    </Typography>
                    <Box sx={{ gridArea: 'bar', display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', gap: '2px', bgcolor: 'action.hover' }}>
                      {segments.map((s) => {
                        const n = b[s.key];
                        return n > 0 ? (
                          <Tooltip key={s.key} title={`${b.block} · ${s.label}: ${n}`} arrow>
                            <Box sx={{ flexGrow: n, flexBasis: 0, bgcolor: s.color }} />
                          </Tooltip>
                        ) : null;
                      })}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ gridArea: 'counts', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {t('ppl.blockCounts', { units: b.units, residents: b.residents })}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Who to invite next: occupied houses with nobody registered. */}
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>{t('ppl.missingTitle')}</Typography>
                <Button size="small" component={RouterLink} to="/residents">{t('ppl.missingAction')}</Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                {summary.occupiedWithoutResident.length === 0 ? t('ppl.missingNone') : t('ppl.missingHelp')}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ maxHeight: 240, overflowY: 'auto' }}>
                {summary.occupiedWithoutResident.map((u) => (
                  <Chip key={u.id} label={u.label} size="small" variant="outlined" />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      ) : null}
    </Stack>
  );
}
